import MD5 from 'crypto-js/md5';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import SecureIcon from 'assets/icons/base/secure-icon.svg';
import { useComment, useIsMobile, usePayment, useTranslate, useValidate } from 'hooks';
import { useAmount } from 'hooks/useAmount';
import { useAppSelector } from 'store/hooks';
import { clearPhoneMask } from 'utils';
import { Button, Input, OfferLink } from 'views/components/base/ui';

type Plus24FieldsType = {
  login: '';
  passmd5: '';
};

const Plus24Form = () => {
  const { paymentData, acquiring, isPayment, fullAmount } = useAppSelector(
    (state) => state.payment,
  );
  const { fetchSendPayment, isLoading } = usePayment();
  const { isMobile } = useIsMobile();
  const { currentAmountForPayment, isValidCurrentAmount } = useAmount();
  const { comment, isValidComment } = useComment();

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
    setFocus,
  } = useForm<Plus24FieldsType>({
    mode: 'onChange',
    defaultValues: {
      login: '',
      passmd5: '',
    },
  });

  const { t } = useTranslate();
  const { validatePlus24Login } = useValidate();

  useEffect(() => {
    Analytics.sentEvent(ANALYTIC_EVENTS.paymentFormPlus24Pay);
  }, []);

  useEffect(() => {
    if (!errors.login && dirtyFields?.login) {
      setFocus('passmd5');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.login]);

  const sendPayment = async (data: Plus24FieldsType) => {
    const payData = {
      phone: '',
      email: '',
      description: comment,
      ID: paymentData?.ID,
      acquiringID: acquiring?.ID,
      amount: currentAmountForPayment,
      login: clearPhoneMask(data.login),
      passmd5: MD5(data.passmd5).toString(),
    };

    await fetchSendPayment(payData);
  };

  return (
    <form onSubmit={handleSubmit(sendPayment)} className="md:max-w-[366px] flex flex-col">
      <div className="my-5 md:my-7 gap-6 lg:gap-0 flex flex-col lg:flex-row md:max-w-none">
        <Controller
          name="login"
          control={control}
          rules={{
            required: true,
            validate: validatePlus24Login,
          }}
          render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
            <Input
              label={t('wallet_form.wallet_number')}
              placeholder="+7 (xxx) xxx xx xx"
              mask=" "
              name="cardNumber"
              format="+7 (###) ### ## ##"
              isMobile={!!isMobile}
              containerClassName="flex-shrink-0 lg:max-w-[230px] w-full"
              value={value}
              ref={ref}
              onChange={onChange}
              type="tel"
              error={error?.message}
            />
          )}
        />
        <Controller
          name="passmd5"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
            <Input
              label={t('password')}
              placeholder={t('enter_password')}
              name="passmd5"
              mask=" "
              value={value}
              isMobile={!!isMobile}
              onChange={onChange}
              ref={ref}
              type="password"
              error={error?.message}
            />
          )}
        />
      </div>
      <div className="text-[10px] font-normal leading-3 text-dark/60 flex items-center gap-2">
        <img src={SecureIcon} width={18} height={18} alt="secure" />
        {t('lang.card_data_encrypted')}
      </div>
      <div className="mt-auto md:mt-8">
        <Button
          isLoading={isLoading}
          className="mb-2 md:max-w-[224px]"
          disabled={!isValid || !isValidCurrentAmount || !isValidComment}
          onClick={handleSubmit(sendPayment)}
        >
          {isPayment ? t('pay') : t('pay_out')}{' '}
          {isMobile && fullAmount && isValid ? `${fullAmount} KZT` : null}
        </Button>
        <OfferLink />
      </div>
    </form>
  );
};

export { Plus24Form };
